import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Loader2, ChevronsUpDown } from 'lucide-react';
import { Button, Input, Popover, PopoverTrigger, PopoverContent } from '@hiring-intelligence/ui';
import { crmContactsApi } from '@/services/api';

export interface PickedContact {
	id: string;
	name: string;
	email?: string | null;
	title?: string | null;
}

interface ContactPickerProps {
	/** Scope the search to one company's contacts (also naturally excludes vault candidates, which have no company). */
	companyId?: string;
	/** Called with the chosen contact; the popover closes afterwards. */
	onSelect: (contact: PickedContact) => void;
	/** Contact ids to hide from the results (e.g. already-added PICs). */
	excludeIds?: string[];
	label?: string;
	disabled?: boolean;
	className?: string;
}

/**
 * Searchable picker for selecting an EXISTING contact (built on Popover + a query box, since the
 * UI kit has no combobox). Loads options from `crmContactsApi.list({ companyId, q })` so a client
 * already in `contacts` can be reused as a PIC / primary contact instead of typed in again.
 */
export function ContactPicker({ companyId, onSelect, excludeIds = [], label = 'Select existing contact', disabled, className }: ContactPickerProps) {
	const [open, setOpen] = useState(false);
	const [q, setQ] = useState('');

	const { data, isLoading } = useQuery({
		queryKey: ['contact-picker', { companyId, q }],
		queryFn: () => crmContactsApi.list({ companyId, q: q || undefined, limit: 50 }),
		enabled: open
	});

	const options: PickedContact[] = (data?.data || []).filter((c: any) => !excludeIds.includes(c.id));

	return (
		<Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(''); }}>
			<PopoverTrigger asChild>
				<Button type="button" variant="outline" size="sm" disabled={disabled} className={className}>
					{label}
					<ChevronsUpDown className="ml-2 h-3.5 w-3.5 opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-80 p-0" align="start">
				<div className="border-b p-2">
					<div className="relative">
						<Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
						<Input
							autoFocus
							placeholder="Search contacts…"
							value={q}
							onChange={(e) => setQ(e.target.value)}
							className="h-8 pl-7 text-sm"
						/>
					</div>
				</div>
				<div className="max-h-64 overflow-auto py-1">
					{isLoading ? (
						<div className="flex items-center gap-2 px-3 py-3 text-sm text-muted-foreground">
							<Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
						</div>
					) : options.length === 0 ? (
						<p className="px-3 py-3 text-sm text-muted-foreground">
							No contacts found{companyId ? ' for this company' : ''}.
						</p>
					) : (
						options.map((c) => (
							<button
								key={c.id}
								type="button"
								onClick={() => { onSelect(c); setOpen(false); setQ(''); }}
								className="flex w-full flex-col items-start px-3 py-1.5 text-left hover:bg-muted focus:bg-muted focus:outline-none"
							>
								<span className="w-full truncate text-sm font-medium">{c.name}</span>
								{(c.title || c.email) && (
									<span className="w-full truncate text-xs text-muted-foreground">
										{[c.title, c.email].filter(Boolean).join(' · ')}
									</span>
								)}
							</button>
						))
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
}
